import API from '@Services/apiAxios';
import dayjs, { Dayjs } from 'dayjs';
import { FedXInformationType, DHLInformationType } from '@Reducers/AdminShipment/Type';
import { AramexInformation } from '../components/Aramex/AramexInformation';

export interface AwbPrefillData {
  shipmentNumber?: string;
  status?: string;
  csp?: string;
  shipmentMode?: string;
  shipmentRouteType?: string;
  purposeOfShipment?: string | null;
  countryOfManufacture?: string | null;
  dutyCharge?: {
    type?: string | null;
    charge?: number | null;
  } | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  shipper?: {
    name?: string | null;
    email?: string | null;
    phone?: {
      telephoneCode?: string | null;
      number?: string | null;
    } | null;
    companyName?: string | null;
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    pincode?: string | null;
    idType?: string | null;
    idNumber?: string | null;
  } | null;
  consignee?: {
    name?: string | null;
    email?: string | null;
    phone?: {
      telephoneCode?: string | null;
      number?: string | null;
    } | null;
    companyName?: string | null;
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    pincode?: string | null;
    idType?: string | null;
    idNumber?: string | null;
  } | null;
  documents?: {
    shipperId?: {
      type?: string | null;
      number?: string | null;
    } | null;
    consigneeId?: {
      type?: string | null;
      number?: string | null;
    } | null;
    kyc?: any[];
  } | null;
  service?: {
    serviceName?: string | null;
    serviceCode?: string | null;
    courierId?: string | null;
    carrierId?: string | null;
  } | null;
  systems?: Array<{
    _id?: string;
    name?: string;
  }> | null;
  form?: Record<string, any> | null;
  vendorAccounts?: any[];
  suggestedVendorAccountNo?: string | null;
  matrix?: any;
  constraints?: any;
  meta?: {
    maxBoxWeightKg?: number;
    isImport?: boolean;
    isExport?: boolean;
    resolvedAccountToken?: string;
    warnings?: any[];
    hasCarrierRules?: boolean;
  } | null;
}

export interface AwbPrefillResponse {
  msg?: string;
  data?: AwbPrefillData;
}

/**
 * Filter out undefined and null values from an object so they do not overwrite default values.
 */
function cleanObject<T extends Record<string, any>>(obj: T): Partial<T> {
  const result: Partial<T> = {};
  for (const key of Object.keys(obj) as Array<keyof T>) {
    const value = obj[key];
    if (value !== undefined && value !== null) {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Fetch AWB Prefill data from API.
 */
export const fetchAwbPrefill = async (shipmentNumber: string, csp: string): Promise<AwbPrefillData | null> => {
  if (!shipmentNumber || !csp) return null;
  try {
    const response = await API.get<AwbPrefillResponse>(`/shipment/admin/awb-prefill/${shipmentNumber}?csp=${csp}`);
    return response.data?.data || null;
  } catch (error) {
    console.error('Error fetching AWB prefill data:', error);
    return null;
  }
};

/**
 * Map API response to FedEx form values.
 */
export const mapPrefillToFedEx = (data: AwbPrefillData): Partial<FedXInformationType> => {
  const form = data.form || {};

  const pickupDateValue = form.pickupDate ? dayjs(form.pickupDate) : undefined;
  const shipmentTypeVal = Array.isArray(form.shipmentType)
    ? form.shipmentType
    : typeof form.shipmentType === 'string' && form.shipmentType
    ? [form.shipmentType]
    : undefined;

  const rawMapped: Partial<FedXInformationType> = {
    accountNo: form.accountNo ?? data.suggestedVendorAccountNo ?? undefined,
    countryOfManufacture: form.countryOfManufacture ?? data.countryOfManufacture ?? undefined,
    shipmentPurpose: form.purposeOfShipment ?? form.shipmentPurpose ?? data.purposeOfShipment ?? undefined,
    payor: form.payor ?? undefined,
    thirdPartyPayor: form.thirdPartyPayor ?? undefined,
    billDutiesAndTaxesTo: form.billDutiesAndTaxesTo ?? undefined,
    billDutiesAccountNo: form.billDutiesAccountNo ?? undefined,
    poNo: form.poNo ?? undefined,
    packagingType: form.packagingType ?? undefined,
    serviceType: form.serviceType ?? undefined,
    csDetails: form.csDetails ?? undefined,
    invoiceNo: form.invoiceNo ?? form.invoiceNumber ?? data.invoiceNumber ?? undefined,
    shipperShipmentType: form.shipperShipmentType ?? undefined,
    shipmentIdType: form.shipmentIdType ?? data.documents?.shipperId?.type ?? data.shipper?.idType ?? undefined,
    shipmentId: form.shipmentId ?? data.documents?.shipperId?.number ?? data.shipper?.idNumber ?? undefined,
    pickupType: form.pickupType ?? undefined,
    pickupDate: pickupDateValue,
    printer: form.printer ?? undefined,
    b13AFilingOption: form.b13AFilingOption ?? undefined,
    bookingConfirmationNumber: form.bookingConfirmationNumber ?? undefined,
    exportComplianceStatement: form.exportComplianceStatement ?? undefined,
    shipmentType: shipmentTypeVal,
  };

  return cleanObject(rawMapped);
};

/**
 * Map API response to DHL form values.
 */
export const mapPrefillToDhl = (data: AwbPrefillData): Partial<DHLInformationType> => {
  const form = data.form || {};

  let paymentMode: string | undefined = form.paymentMode;
  if (!paymentMode && form.payor) {
    if (form.payor === 'SENDER') paymentMode = 'S';
    else if (form.payor === 'RECIPIENT') paymentMode = 'R';
    else paymentMode = form.payor;
  }

  let dutyPaymentMode: string | undefined = form.dutyPaymentMode;
  if (!dutyPaymentMode) {
    const dutyType = form.dutyCharge?.type ?? data.dutyCharge?.type;
    if (dutyType === 'DDP') dutyPaymentMode = 'DD';
    else if (dutyType === 'DDU') dutyPaymentMode = 'DS';
  }

  const rawMapped: Partial<DHLInformationType> = {
    accountNo: form.accountNo ?? data.suggestedVendorAccountNo ?? undefined,
    countryOfManufacture: form.countryOfManufacture ?? data.countryOfManufacture ?? undefined,
    paymentMode: paymentMode ?? undefined,
    ShipperType: form.ShipperType ?? form.shipperType ?? undefined,
    GSTINType: form.GSTINType ?? form.gstinType ?? data.documents?.shipperId?.type ?? data.shipper?.idType ?? undefined,
    GSTINNumber:
      form.GSTINNumber ?? form.gstinNumber ?? data.documents?.shipperId?.number ?? data.shipper?.idNumber ?? undefined,
    ShipmentType: form.ShipmentType ?? form.shipmentType ?? undefined,
    dutyPaymentMode: dutyPaymentMode ?? undefined,
    gstInvNo: form.gstInvNo ?? form.invoiceNumber ?? data.invoiceNumber ?? undefined,
    gstInvNoDate: form.gstInvNoDate ?? form.invoiceDate ?? data.invoiceDate ?? undefined,
  };

  return cleanObject(rawMapped);
};

/**
 * Map API response to Aramex form values.
 */
export const mapPrefillToAramex = (data: AwbPrefillData): Partial<AramexInformation> => {
  const form = data.form || {};

  let paymentType: string | undefined = form.paymentType;
  if (!paymentType && form.payor) {
    if (form.payor === 'SENDER') paymentType = 'P';
    else if (form.payor === 'RECIPIENT') paymentType = 'C';
  }

  const invoiceDateValue: Dayjs | null | undefined = form.invoiceDate
    ? dayjs(form.invoiceDate)
    : data.invoiceDate
    ? dayjs(data.invoiceDate)
    : undefined;

  const serviceTypeVal = form.serviceType ?? form.dutyCharge?.type ?? data.dutyCharge?.type ?? undefined;

  const billToAddressVal =
    form.BillToAddress ??
    form.billToAddress ??
    (data.consignee
      ? [data.consignee.line1, data.consignee.line2, data.consignee.city, data.consignee.state, data.consignee.pincode]
          .filter(Boolean)
          .join(', ')
      : undefined);

  const rawMapped: Partial<AramexInformation> = {
    accountNo: form.accountNo ?? data.suggestedVendorAccountNo ?? undefined,
    countryOfManufacture: form.countryOfManufacture ?? data.countryOfManufacture ?? undefined,
    shipperTaxId: form.shipperTaxId ?? data.documents?.shipperId?.number ?? data.shipper?.idNumber ?? undefined,
    invoiceDate: invoiceDateValue,
    productType: form.productType ?? undefined,
    paymentType: paymentType ?? undefined,
    invoiceNumber: form.invoiceNumber ?? form.invoiceNo ?? data.invoiceNumber ?? undefined,
    ShipmentType: form.ShipmentType ?? form.shipmentType ?? undefined,
    serviceType: serviceTypeVal,
    WhetherSupplyforExportIsOnPaymentOfIGST:
      form.WhetherSupplyforExportIsOnPaymentOfIGST ?? form.whetherSupplyforExportIsOnPaymentOfIGST ?? undefined,
    WhetherAgainstBondOrUT: form.WhetherAgainstBondOrUT ?? form.whetherAgainstBondOrUT ?? undefined,
    TermsOfTrade: form.TermsOfTrade ?? form.termsOfTrade ?? undefined,
    WhetherMEIS: form.WhetherMEIS ?? form.whetherMEIS ?? undefined,
    IGSTRate: form.IGSTRate != null ? Number(form.IGSTRate) : undefined,
    IGSTAmount: form.IGSTAmount != null ? Number(form.IGSTAmount) : undefined,
    ADCode: form.ADCode ?? form.adCode ?? undefined,
    FreightAmount: form.FreightAmount != null ? Number(form.FreightAmount) : undefined,
    InsuranceAmount: form.InsuranceAmount != null ? Number(form.InsuranceAmount) : undefined,
    BillToName: form.BillToName ?? form.billToName ?? data.consignee?.name ?? undefined,
    BillToAddress: billToAddressVal || undefined,
    BillToCountryCode: form.BillToCountryCode ?? form.billToCountryCode ?? data.consignee?.country ?? undefined,
    BankAccountNumber: form.BankAccountNumber ?? form.bankAccountNumber ?? undefined,
    GSTN: form.GSTN ?? form.gstn ?? undefined,
    IEC: form.IEC ?? form.iec ?? undefined,
  };

  return cleanObject(rawMapped);
};
